import { z } from "zod";

/**
 * Per-org budget cap + running counters for AI inference triggered by
 * workflow-preset materialised nodes (currently the AI-passive
 * satisfaction scorer; future presets will consult the same envelope).
 *
 * Persisted on `Organization.aiEvaluationBudget`. Caps are admin-managed
 * via the console-ui surface; running counters are mutated by
 * `CheckEvaluationBudgetUseCase` on each pre-flight + reset by the daily
 * counter-reset cron.
 *
 * A cap of `0` means uncapped — the equivalent of "no enforcement". The
 * pre-flight check short-circuits with a warn log + admin notification
 * (Echo event `AI_BUDGET_EXCEEDED`) when a running counter is at-or-above
 * its respective cap.
 *
 * USD only — no multi-currency support; the materialiser converts model
 * usage to USD at inference time via the existing pricing adapter.
 *
 * Counter race note: counters are atomically incremented per evaluation
 * via the IncrementEvaluationSpendPrismaAdapter (single jsonb_set SQL,
 * concurrency-safe at the row level). The pre-flight check + increment
 * pair is intentionally not transactional, so the effective cap is soft
 * under high concurrency — multiple inferences can pass pre-flight in
 * the same instant and collectively push the counter slightly past the
 * cap before subsequent calls are blocked. Acceptable given satisfaction
 * scoring's low per-org concurrency. See `evaluate-satisfaction.openai.adapter.ts`
 * for the pre-flight + increment contract.
 */
export const AiEvaluationSpendCounterSchema = z.object({
  usd: z.number().min(0).default(0),
  /** ISO timestamp of the most recent reset by the daily counter-reset cron. */
  lastResetAt: z.string().datetime().optional()
});

export type AiEvaluationSpendCounter = z.infer<typeof AiEvaluationSpendCounterSchema>;

export const AiEvaluationBudgetSchema = z.object({
  /** Daily ceiling in USD. `0` disables the daily cap. */
  dailyUsdCap: z.number().min(0).default(0),
  /** Monthly ceiling in USD. `0` disables the monthly cap. */
  monthlyUsdCap: z.number().min(0).default(0),
  spentToday: AiEvaluationSpendCounterSchema.default({ usd: 0 }),
  spentThisMonth: AiEvaluationSpendCounterSchema.default({ usd: 0 })
});

export type AiEvaluationBudget = z.infer<typeof AiEvaluationBudgetSchema>;
